import { useAuth } from "../lib/auth";
import { Bell, Search, ChevronDown, Activity, LogOut, Building2, ShieldCheck, Briefcase, AlertTriangle, Cpu, FlaskConical, RefreshCw } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";

export default function TopBar({ onOpenSearch }) {
  const { user, institution, logout, switchInstitution, examinerMode, toggleExaminer } = useAuth();
  const [openInst, setOpenInst] = useState(false);
  const [openUser, setOpenUser] = useState(false);
  const [openModel, setOpenModel] = useState(false);
  const [openNotif, setOpenNotif] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [institutions, setInstitutions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/institutions").then((r) => setInstitutions(r.data.institutions || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.email) return;
    const load = () => api.get(`/notifications?user=${encodeURIComponent(user.email)}`).then(r => {
      setNotifs(r.data.notifications || []);
      setUnread(r.data.unread_count || 0);
    }).catch(() => {});
    load();
    const id = setInterval(load, 45000);
    return () => clearInterval(id);
  }, [user?.email]);

  const iconFor = (k) => k === "checker" ? ShieldCheck : k === "case" ? Briefcase : k === "mlro" ? AlertTriangle : Cpu;

  const instName = institution?.name || "Acme Demo Bank Plc";

  return (
    <header data-testid="top-bar" className="h-14 bg-navy grain-dark text-white flex items-stretch border-b border-navy-800 shrink-0">
      <div className="px-4 flex items-center gap-3 border-r border-white/8 min-w-[280px]">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-amber/20 border border-amber/40 flex items-center justify-center">
            <div className="grid grid-cols-3 gap-[1.5px] w-3 h-3">
              {[1,1,1, 1,0,1, 1,1,1].map((v,i) => <span key={i} className={`w-[3px] h-[3px] ${v ? 'bg-amber' : 'bg-transparent'}`} />)}
            </div>
          </div>
          <span className="display text-white font-semibold text-[15px] tracking-tightest">Atom</span>
          <span className="text-white/55 text-[12px]">·</span>
          <span className="text-white/85 text-[12px] tracking-tight">AML Solution</span>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div data-testid="env-pill" className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/12 text-[11px] text-white/72 tracking-tight">
          <span className="w-1.5 h-1.5 rounded-full bg-amber" />
          Live compliance workspace · 90-day operating window
        </div>
      </div>
      <div className="flex items-center gap-1 px-3 border-l border-white/10">
        {/* Demo mode pill */}
        <button data-testid="demo-mode-btn" onClick={async () => {
          if (window.confirm("Reset demo data?\n\nThis will clear all maker-checker mutations and SAR signoffs back to seeded baseline. Use before re-demoing to a new prospect.")) {
            try { await api.post("/demo/reset"); window.location.reload(); } catch(e) {}
          }
        }} className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] text-amber-200 hover:bg-amber/15 rounded border border-amber/25 mr-1" title="Demo mode — reset to seed">
          <FlaskConical size={11} strokeWidth={1.25} />
          DEMO
          <RefreshCw size={9} strokeWidth={1.25} className="ml-0.5" />
        </button>
        {/* Institution selector */}
        <div className="relative">
          <button data-testid="institution-toggle" onClick={() => { setOpenInst(!openInst); setOpenUser(false); setOpenModel(false); }} className="flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] text-white/80 hover:bg-white/8 rounded">
            <Building2 size={13} strokeWidth={1.25} />
            <span className="max-w-[180px] truncate">{instName}</span>
            <ChevronDown size={12} />
          </button>
          {openInst && (
            <div className="absolute right-0 top-full mt-1 w-[280px] bg-white text-ink border border-line z-30">
              <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-ink-subtle border-b border-line">View as</div>
              {institutions.map((it) => (
                <button key={it.id} data-testid={`institution-${it.id}`} onClick={() => { switchInstitution(it); setOpenInst(false); }} className="w-full text-left px-3 py-2 text-[13px] hover:bg-bg-hover border-b border-line/50 last:border-0">
                  <div className="font-medium">{it.name}</div>
                  <div className="text-[11px] text-ink-subtle">{it.rc} · {it.branches} branches</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <button onClick={onOpenSearch} data-testid="global-search-btn" className="flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-white/80 hover:bg-white/8 rounded">
          <Search size={13} strokeWidth={1.25} />
          <span>Search</span>
          <kbd className="ml-1 px-1.5 py-0.5 bg-white/10 rounded text-[10px] font-mono">⌘K</kbd>
        </button>

        <div className="relative">
          <button data-testid="notifications-btn" onClick={() => { setOpenNotif(!openNotif); setOpenInst(false); setOpenUser(false); setOpenModel(false); }} className="relative p-1.5 hover:bg-white/8 rounded">
            <Bell size={15} strokeWidth={1.25} />
            {unread > 0 && (
              <span className="absolute top-0 right-0 min-w-[15px] h-[15px] bg-amber rounded-full text-[9px] font-medium flex items-center justify-center px-1 text-ink">{unread}</span>
            )}
          </button>
          {openNotif && (
            <div className="absolute right-0 top-full mt-2 w-[360px] bg-white text-ink border border-line rounded-xl z-30 shadow-[0_4px_12px_rgba(11,43,92,0.08),0_24px_48px_rgba(11,43,92,0.16)] overflow-hidden">
              <div className="px-4 py-3 border-b border-line flex items-center justify-between bg-gradient-to-r from-amber/10 to-white">
                <div>
                  <div className="text-[13px] font-medium">Notifications</div>
                  <div className="text-[10px] text-ink-subtle">{unread} unread · {notifs.length} total</div>
                </div>
                <button onClick={() => { api.post("/notifications/mark-read", { ids: notifs.map(n => n.id) }); setUnread(0); }} className="text-[10px] text-amber-600 hover:underline">Mark all read</button>
              </div>
              <div className="max-h-[380px] overflow-y-auto">
                {notifs.length === 0 ? (
                  <div className="p-6 text-center text-[12px] text-ink-subtle">No notifications</div>
                ) : notifs.map((n) => {
                  const Icon = iconFor(n.kind);
                  return (
                    <Link key={n.id} to={n.link || "#"} onClick={() => setOpenNotif(false)} className={`flex items-start gap-2.5 px-4 py-3 border-b border-line/50 last:border-0 hover:bg-bg-warm transition-colors ${n.unread ? "bg-amber/3" : ""}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                        n.kind === "checker" ? "bg-amber/15 text-amber-600" :
                        n.kind === "mlro" ? "bg-risk-high/10 text-risk-high" :
                        n.kind === "system" ? "bg-bg-warm text-ink-muted" :
                        "bg-info/10 text-info"
                      }`}>
                        <Icon size={13} strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-[12px] font-medium truncate">{n.title}</span>
                          {n.unread && <span className="w-1.5 h-1.5 rounded-full bg-amber shrink-0 mt-1.5" />}
                        </div>
                        <div className="text-[11px] text-ink-muted truncate">{n.body}</div>
                        <div className="text-[10px] text-ink-subtle mt-0.5">{n.ts}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              <div className="px-4 py-2 border-t border-line bg-bg-warm text-center">
                <Link to="/my-work" onClick={() => setOpenNotif(false)} className="text-[11px] text-amber-600 hover:underline">Open my workspace →</Link>
              </div>
            </div>
          )}
        </div>

        {/* Atom AI status */}
        <div className="relative">
          <button data-testid="atom-status-btn" onClick={() => { setOpenModel(!openModel); setOpenInst(false); setOpenUser(false); }} className="flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] text-white/80 hover:bg-white/8 rounded">
            <Activity size={13} strokeWidth={1.25} className="text-risk-low" />
            <span>Atom AI</span>
          </button>
          {openModel && (
            <div className="absolute right-0 top-full mt-1 w-[300px] bg-white text-ink border border-line z-30 p-3">
              <div className="text-[12px] font-medium mb-2">Atom AI series — health</div>
              {[
                ["Atom Electron v1.1", "84ms p95", "Operational"],
                ["Atom Proton v2.4", "340ms p95", "Amber drift"],
                ["Atom Neutron v2.0", "190ms p95", "Operational"],
                ["Atom Nucleus v3.5", "2.1s p95", "Operational"],
                ["Atom Photon v1.0", "—", "Operational"],
              ].map(([n, l, s]) => (
                <div key={n} className="flex items-center justify-between py-1.5 text-[12px] border-b border-line/50 last:border-0">
                  <span className="font-medium">{n}</span>
                  <span className="text-ink-subtle">{l}</span>
                  <span className={`w-1.5 h-1.5 rounded-full ${s.includes('Amber') ? 'bg-risk-medium' : 'bg-risk-low'}`} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* User profile */}
        <div className="relative ml-1 pl-2 border-l border-white/10">
          <button data-testid="user-menu-btn" onClick={() => { setOpenUser(!openUser); setOpenInst(false); setOpenModel(false); }} className="flex items-center gap-2 px-2 py-1.5 hover:bg-white/8 rounded">
            <div className="w-6 h-6 rounded-full bg-amber/20 border border-amber/40 flex items-center justify-center text-[10px] font-medium">
              {user?.name?.split(" ").map(s => s[0]).join("").slice(0,2) || "?"}
            </div>
            <div className="text-left">
              <div className="text-[12px] font-medium leading-tight">{user?.name}</div>
              <div className="text-[10px] text-white/55 leading-tight">{user?.role}</div>
            </div>
            <ChevronDown size={12} className="text-white/55" />
          </button>
          {openUser && (
            <div className="absolute right-0 top-full mt-1 w-[220px] bg-white text-ink border border-line z-30">
              <div className="px-3 py-2 border-b border-line">
                <div className="text-[12px] font-medium">{user?.name}</div>
                <div className="text-[11px] text-ink-subtle">{user?.email}</div>
                <div className="text-[11px] text-ink-subtle mt-0.5">MFA: TOTP enrolled</div>
              </div>
              <button data-testid="examiner-toggle" onClick={() => { toggleExaminer(); setOpenUser(false); }} className="w-full text-left px-3 py-2 text-[13px] hover:bg-bg-hover flex items-center gap-2 border-b border-line">
                <ShieldCheck size={13} strokeWidth={1.5} className={examinerMode ? "text-amber-600" : ""} />
                {examinerMode ? "Exit examiner mode" : "Enter examiner mode"}
              </button>
              <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }} className="w-full text-left px-3 py-2 text-[13px] hover:bg-bg-hover flex items-center gap-2">
                <LogOut size={13} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
